import { useEffect, useRef, type ReactElement } from 'react';
import type { NotificationPage } from '@molt/shared';
import {
  desktopNotificationPermission,
  desktopNotificationTitle,
  notificationHref,
} from '@/lib/notifications';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface DesktopNotificationBridgeProps {
  readonly enabled: boolean;
  readonly page?: NotificationPage | undefined;
  readonly onNavigate?: ((href: string) => void) | undefined;
}

export function DesktopNotificationBridge({
  enabled,
  page,
  onNavigate = (href) => {
    window.location.assign(href);
  },
}: DesktopNotificationBridgeProps): ReactElement | null {
  const { catalogue, t } = useInterfaceLanguage();
  const seen = useRef<Set<string>>(new Set());
  const seeded = useRef(false);

  useEffect(() => {
    const notifications = page?.notifications ?? [];
    if (notifications.length === 0) return;

    if (!seeded.current) {
      for (const notification of notifications) {
        seen.current.add(notification.id);
      }
      seeded.current = true;
      return;
    }

    const canNotify = enabled && desktopNotificationPermission() === 'granted';
    for (const notification of notifications) {
      if (seen.current.has(notification.id)) continue;
      seen.current.add(notification.id);
      if (!canNotify || notification.read) continue;

      const href = notificationHref(notification);
      const body = notification.comment?.excerpt ?? notification.card?.title;
      const options: NotificationOptions = { tag: notification.id };
      if (body !== undefined) options.body = body;
      const title = desktopNotificationTitle(notification, catalogue, t);
      let desktop: Notification;
      try {
        desktop = new window.Notification(title, options);
      } catch {
        /*
         * Android Chrome — the installed PWA included — throws `Illegal
         * constructor` here: a page may only show a notification through its
         * service worker. The click then lands in `sw.js`'s
         * `notificationclick`, which routes via the `href` in `data`.
         * `getRegistration`, never `.ready`: `ready` never settles where no
         * worker is registered (every http origin), and this effect must not
         * leave a promise hanging per notification.
         */
        void navigator.serviceWorker
          ?.getRegistration()
          .then((registration) =>
            registration?.showNotification(title, { ...options, data: { href } }),
          )
          .catch(() => undefined);
        continue;
      }
      desktop.onclick = () => {
        window.focus();
        if (href !== null) onNavigate(href);
      };
    }
  }, [catalogue, enabled, onNavigate, page]);

  return null;
}
